import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import logger from '../../../../Helpers/logger';
import { rotateImage } from './utils';

const INIT_CROP = { unit: '%', x: 0, y: 0, width: 100, height: 100 };

const ImageEditor = ({ file, onEditComplete, onRegisterApply }) => {
    const imageRef = useRef(null);
    const completedCropRef = useRef(null);
    const applyFnRef = useRef(null);
    const rotatedFileRef = useRef(null);
    const wrapperRef = useRef(null);

    const [displaySrc, setDisplaySrc] = useState('');
    const [crop, setCrop] = useState(INIT_CROP);
    const [imgMaxHeight, setImgMaxHeight] = useState(undefined);

    useEffect(() => {
        if (!file) return;

        const url = URL.createObjectURL(file);
        setDisplaySrc(url);

        return () => URL.revokeObjectURL(url);
    }, [file]);

    applyFnRef.current = () => applyCrop();

    useEffect(() => {
        onRegisterApply?.(() => applyFnRef.current?.());
        return () => onRegisterApply?.(null);
    }, []);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const update = () => {
            const h = el.offsetHeight;
            if (h > 0) setImgMaxHeight(h);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const createFileFromBlob = blob => new File([blob], file.name, { type: file.type, lastModified: Date.now() });

    const applyCrop = () => {
        return new Promise(resolve => {
            const cropData = completedCropRef.current;

            const image = imageRef.current;

            if (!cropData || !imageRef.current) {
                const rotated = rotatedFileRef.current;
                if (rotated) {
                    onEditComplete?.(rotated);
                    resolve(rotated);
                } else {
                    resolve(file);
                }
                return;
            }

            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            const canvas = document.createElement('canvas');

            const cropWidth = Math.max(1, cropData.width * scaleX);
            const cropHeight = Math.max(1, cropData.height * scaleY);

            canvas.width = cropWidth;
            canvas.height = cropHeight;

            const ctx = canvas.getContext('2d');

            ctx.drawImage(
                image,
                cropData.x * scaleX,
                cropData.y * scaleY,
                cropWidth,
                cropHeight,
                0,
                0,
                cropWidth,
                cropHeight,
            );

            canvas.toBlob(
                blob => {
                    if (!blob) {
                        resolve(file);
                        return;
                    }

                    const editedFile = new File([blob], file.name, {
                        type: file.type,
                        lastModified: Date.now(),
                    });

                    onEditComplete?.(editedFile);
                    resolve(editedFile);
                },
                file.type || 'image/jpeg',
                0.92,
            );
        });
    };

    const rotate = async deg => {
        try {
            const blob = await rotateImage(displaySrc, deg, file.type);
            const newFile = createFileFromBlob(blob);
            rotatedFileRef.current = newFile;
            setDisplaySrc(URL.createObjectURL(newFile));
            completedCropRef.current = null;
            setCrop(INIT_CROP);
        } catch (e) {
            logger.error(e);
        }
    };

    return (
        <div className="se-crop-editor">
            <div className="se-crop-wrapper" ref={wrapperRef}>
                {displaySrc && (
                    <ReactCrop
                        crop={crop}
                        keepSelection
                        minWidth={50}
                        minHeight={50}
                        onChange={(_, pct) => setCrop(pct)}
                        onComplete={c => {
                            completedCropRef.current = c;
                        }}
                    >
                        <img
                            ref={imageRef}
                            src={displaySrc}
                            alt="Crop"
                            style={{
                                maxWidth: '100%',
                                maxHeight: imgMaxHeight != null ? `${imgMaxHeight}px` : undefined,
                                display: 'block',
                            }}
                        />
                    </ReactCrop>
                )}
            </div>

            <div className="se-crop-bottom">
                <button type="button" className="se-rotate-btn" onClick={() => rotate(-90)} title="Rotate left">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47zM6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47zm1.01 5.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32zM13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z" />
                    </svg>
                </button>

                <button type="button" className="se-rotate-btn" onClick={() => rotate(90)} title="Rotate right">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zM13 17.9v2.02c1.39-.17 2.74-.71 3.9-1.61l-1.44-1.44c-.75.54-1.59.89-2.46 1.03zm3.89-2.42l1.42 1.41c.9-1.16 1.45-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.48z" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

ImageEditor.propTypes = {
    file: PropTypes.object,
    onEditComplete: PropTypes.func,
    onRegisterApply: PropTypes.func,
};

export default ImageEditor;
